/**
 * Blueprints: everything the build menu offers, one per machine type in the rules' table. The menu
 * lists them, placing previews and places them, and the world draws their ghosts, all from this
 * one table: a new machine is a row in the rules, a name in i18n and a model in the world.
 */
import {
  emptyPockets,
  lacks,
  MACHINE_TYPES,
  MACHINES,
  missing,
  type Action,
  type Cost,
  type CrewState,
  type MachineType,
} from '@bramvasterd/terrella-rules';

/** One thing to build: a machine of a type. */
export interface Blueprint {
  kind: 'machine';
  type: MachineType;
}

/** The blueprints on offer, in the build menu's order. */
export const BLUEPRINTS: readonly Blueprint[] = MACHINE_TYPES.map((type) => ({
  kind: 'machine',
  type,
}));

/** A blueprint's stable name, for lists, the DOM and tests: "machine:crystal". */
export const blueprintId = (b: Blueprint): string => `${b.kind}:${b.type}`;

/** The rules' action that places it on `tile`. */
export function placeAction(b: Blueprint, tile: number): Action {
  return { kind: 'place', tile, machine: b.type };
}

/** What placing it costs: an extractor's level 1, any other machine's own. */
export function costOf(b: Blueprint): Cost {
  const spec = MACHINES[b.type];
  return spec.role === 'extractor' ? (spec.levels[0]?.cost ?? {}) : spec.cost;
}

export interface BlueprintStatus {
  cost: Cost;
  /** What my pockets lack for it. */
  missing: Cost;
}

/** For the build menu: what it costs me, and what I lack for it (nothing in admin mode). */
export function blueprintStatus(
  state: CrewState,
  player: string | null,
  b: Blueprint,
): BlueprintStatus {
  const cost = costOf(b);
  return {
    cost,
    missing: player !== null ? lacks(state, player, cost) : missing(emptyPockets(), cost),
  };
}
